import {
    Button, Modal, App, Form, Input, DatePicker, Radio, Switch, Tag, Row, Col, Table, Typography,
    Alert, Select 
  } from 'antd';
import {
    CheckOutlined, CloseOutlined, FormOutlined, SearchOutlined
  } from '@ant-design/icons';    
import React, { useState, useEffect, useCallback  } from "react";  
import { useTranslation } from "react-i18next";
import dayjs from 'dayjs';
import OdoReadingField from "../../components/panels/OdoReadingField";
import { ValidatePhoneNumber, getSessionData, createSelectOptions } from "../../utility/global-util";
import TimeOfDayOptions from "../../components/data/TimeOfDayOptions";
import { submitMaintenanceRequest } from "../../api/maintenance";
import { showApiError } from "../../utility/error/error-handler";
import { checkLocationServiceAvailable, getAddressfromProvider, getCountries, doAddressSearch, } from '../../api/location';
import ClearAddressLink from '../../components/data/ClearAddressFields'


const TyreRequestDetails = ({vehicle, driver, tyreRequestDetailsModalVisible, setTyreRequestDetailsModalVisible, currentTyreRequest, setIsModalVisible,
                             submitProgress, setSubmitProgress, setCurrentTyreRequest, required}) => {
    const { Text } = Typography;
    const { t } = useTranslation();
    const { message, notification } = App.useApp();
    const [tyreRequestDetailsForm] = Form.useForm();
    const [addressRequired, setAddressRequired] = useState(false);
    const [selectedAddress, setSelectedAddress] = useState(false);
    const [lookupAvailable, setLookupAvailable] = useState(false);
    const [resultsLoading, setResultsLoading] = useState(100);
    const [warningMessage, setWarningMessage] = useState(null);
    const [addressOptions, setAddressOptions] = useState([]);
    const [countryInfo, setCountryInfo] = useState(null);
    const [tyreWorkLocation, setTyreWorkLocation] = useState(null);
    const showAddressFinder = lookupAvailable && addressRequired && !selectedAddress;
    const showClearAddress = addressRequired;
    const showAddress = (lookupAvailable && addressRequired && selectedAddress) || 
                        (!lookupAvailable && addressRequired);    
    const tyresBeingRepaired = currentTyreRequest?.tyres;

    // const newWorkLocation = tyreRequestDetailsForm.getFieldValue('workLocation');

    const disabledDate = (current) => {
        // Can not select days before today + 7 days
        const dateIn7days = new Date();
        dateIn7days.setDate(dateIn7days.getDate() + 7);
      
        return current && current < dayjs(dateIn7days).endOf('day');
    };


    useEffect(() => {
        checkLocationServiceAvailable().then(res => {
            setLookupAvailable(res.result);
        }
    );
        getCountries().then(res => {
            setCountryInfo(res);
        });
    }, [setLookupAvailable]);

    useEffect(() => {
        if (!selectedAddress)
        {
            setSelectedAddress(null);
        }
    }, [selectedAddress, lookupAvailable]);

    function handleLocationChange(response) 
    {
        // when user toggles between supplier / vehicle ensure relevant values are reset i.e. address search facility, address fields, select address
        setAddressRequired(false);
        setSelectedAddress(null);
        setAddressOptions(null);
        tyreRequestDetailsForm.setFieldValue('line1', null);
        tyreRequestDetailsForm.setFieldValue('line2', null);
        tyreRequestDetailsForm.setFieldValue('line3', null);
        tyreRequestDetailsForm.setFieldValue('townOrCity', null);
        tyreRequestDetailsForm.setFieldValue('region', null);
        tyreRequestDetailsForm.setFieldValue('postcode', null);
        tyreRequestDetailsForm.setFieldValue('countryCode', null);

        setTyreWorkLocation(response);
        if (response === 'AT_VEHICLE') {
            setAddressRequired(true);
        }
    }

    function ClearAddress() 
    {
        // When user clicks the clear address link ensure the address options are reset to when initially intend capturing an address i.e. address search facility, address fields, select address
        if (lookupAvailable && selectedAddress) {
            setSelectedAddress(null);
            setAddressOptions(null);
        }

        tyreRequestDetailsForm.setFieldValue('line1', null);
        tyreRequestDetailsForm.setFieldValue('line2', null);
        tyreRequestDetailsForm.setFieldValue('line3', null);
        tyreRequestDetailsForm.setFieldValue('townOrCity', null);
        tyreRequestDetailsForm.setFieldValue('region', null);
        tyreRequestDetailsForm.setFieldValue('postcode', null);
        tyreRequestDetailsForm.setFieldValue('countryCode', null);
    }    

    const selectAddress = useCallback((providerIdentifier) => {
    setResultsLoading(0);
    getAddressfromProvider(tyreRequestDetailsForm.getFieldValue('postcode'), providerIdentifier, setResultsLoading).then(res => {

        // This code is relevant in re-populatign the form upon selecting an address as the state change clears the fields already captured
        const newOdoReading = tyreRequestDetailsForm.getFieldValue('newOdo');
        const newPreferredDate = tyreRequestDetailsForm.getFieldValue('preferredDate');
        const newPreferredTimeOfDay = tyreRequestDetailsForm.getFieldValue('preferredTimeOfDay');
        const newWorkLocation = tyreRequestDetailsForm.getFieldValue('workLocation');

        setSelectedAddress(res);
        Object.entries(res).map((entry) => {
            tyreRequestDetailsForm.setFieldValue(entry.key, entry.value);
            return 'test';
        })

        // This code is relevant in re-populatign the form upon selecting an address as the state change clears the fields already captured
        tyreRequestDetailsForm.setFieldValue('newOdo', newOdoReading);
        tyreRequestDetailsForm.setFieldValue('preferredDate', newPreferredDate);
        tyreRequestDetailsForm.setFieldValue('preferredTimeOfDay', newPreferredTimeOfDay);
        tyreRequestDetailsForm.setFieldValue('workLocation', newWorkLocation);
        
    })
    }, [setSelectedAddress, tyreRequestDetailsForm]);

    const lookupAddress = useCallback(() => {
    if (!tyreRequestDetailsForm.getFieldValue('postcode'))
    {
        tyreRequestDetailsForm.validateFields();
    }
    else
    {
        const params = tyreRequestDetailsForm.getFieldValue('line1') ? {line1: tyreRequestDetailsForm.getFieldValue('line1'), locale: getSessionData('userLocale')} : {locale: getSessionData('userLocale')};
        
        setResultsLoading(0);
        doAddressSearch(tyreRequestDetailsForm.getFieldValue('postcode'), params, setResultsLoading).then((res) => {
            setWarningMessage(null);
            if (res.length === 1)
            {
                if (res[0].key)
                {
                    selectAddress(res[0].key);
                }
                else
                {
                    setWarningMessage(res[0].description);
                }
            }
            else
            {
                setAddressOptions(res);
            }
        });
    }
    }, [setAddressOptions, tyreRequestDetailsForm, selectAddress]);

    const onFinish = (values) => {
        let reconstructedSelectedAddress = {};

        if (!selectedAddress && addressRequired  && required)
        {
            lookupAddress(); 
            return;
        }

        // this code was specificailly added for when postcode lookup is NOT enabled and the driver captures an address manually
        // and then when the driver clicked next button, nothing happened, reason being selectedAddress was null and 
        // the onFinish method checked if (!selectedAddress && currentMethod === 'add' && required)
        if (addressRequired && selectedAddress === null)
        {
           reconstructedSelectedAddress = {
                    line1: tyreRequestDetailsForm.getFieldValue('line1'),
                    line2: tyreRequestDetailsForm.getFieldValue('line2'),
                    line3: tyreRequestDetailsForm.getFieldValue('line3'),
                    townOrCity: tyreRequestDetailsForm.getFieldValue('townOrCity'),
                    region: tyreRequestDetailsForm.getFieldValue('region'),
                    postcode: tyreRequestDetailsForm.getFieldValue('postcode'), 
                    countryCode: tyreRequestDetailsForm.getFieldValue('countryCode'), 
            };
        }
        
        notification.destroy();
        //Wrap up nicely tyre requirements and submit request with tyre details contained
        currentTyreRequest = {
            ...currentTyreRequest,
            ...values,
            workRequired: 'TYRE REQUEST',  // Will be updated by biz-tier with all tyre requests and their outcomes
            contactPhone: values.contactNumber,
            latestOdoReading: values.newOdo,
            isCollectionAndDeliveryRequired: values.collectionDeliveryRequired,
            workLocationAddress: selectedAddress ? {
                line1: values.line1,
                line2: values.line2,
                line3: values.line3,
                townOrCity: values.townOrCity,
                region: values.region,
                postcode: values.postcode,
                countryCode: values.countryCode,
            } : reconstructedSelectedAddress,

        };
        delete currentTyreRequest.popupTyre;

        submitMaintenanceRequest(vehicle.vehicleId, currentTyreRequest, setSubmitProgress)
        .then(res =>{
           message.success(t('message.successfully_submitted', {0: t('field.booking_request')}));
           setIsModalVisible(false);
           setTyreRequestDetailsModalVisible(false);
           notification.destroy();
           setCurrentTyreRequest({});  // resets the tyre object 
           tyreRequestDetailsForm.resetFields();
           setAddressRequired(false);
           setSelectedAddress(null);
           setAddressOptions(null);
           setTyreWorkLocation(null);
           })
        .catch(err => showApiError(t('title.new_booking_request'), err, notification));
    }

    const TyreRequestForm = ({onFinish, driver}) => {
        return (
                <div>
                    <div className='tyre-body'>
                        <Form form={tyreRequestDetailsForm}
                              id="tyreRequestDetails"
                              layout="vertical"
                              onFinish={onFinish}
                        >
                            <OdoReadingField name={'newOdo'} required={true} vehicle={vehicle} width="200px"/>
                            <Form.Item name="contactNumber" 
                               label={t('field.contact_number')}
                               initialValue={driver.mobilePhone}
                               rules={[
                                    { required: true, message: t('error.no_value_named', {0: t('field.contact_number')}) },
                                    () => ({
                                    validator(_, value) {
                                        return ValidatePhoneNumber(value, t);
                                    },
                                }),]}
                            >
                                <Input allowClear={true}
                                       type="tel"
                                       maxLength={50}
                                       placeholder={t('action.enter_value')} 
                                />
                            </Form.Item>
                            <Form.Item name="preferredDate" 
                                    label={t('field.preferred_date')}
                                    rules={[
                                            { required: true, message: t('error.no_value_named', {0: t('field.preferred_date') }) }]}
                            >
                                <DatePicker format={t('pattern.date_only').toUpperCase()}
                                            placeholder={t('action.enter_value')}
                                            disabledDate={disabledDate}
                                            onChange={(date, dateString) => console.log(date, dateString)}
                                />
                            </Form.Item>
                              <Form.Item name="preferredTimeOfDay" 
                                    label={t('field.preferred_time')}
                                    rules={[
                                            { required: true, message: t('error.no_value_named', {0: t('field.preferred_time') }) }]}
                            >
                                <Radio.Group options={TimeOfDayOptions(t('action.time_of_day_morning'), t('action.time_of_day_afternoon'), t('action.time_of_day_anytime'))}
                                            optionType="button"
                                            buttonStyle="solid"
                                >
                                </Radio.Group>
                            </Form.Item> 
                            <Form.Item name="workLocation"
                                    style={{ fontSize: '20px' }}
                                    label={ t('field.work_location')}
                                    rules={[
                                        { required: true, message: t('error.no_value_named', {0: t('field.work_location')})},
                                        ]}
                        >
                            <Radio.Group 
                                options={[{value:'AT_SUPPLIER', key:'AT_SUPPLIER', label: t('value.at_supplier')}, 
                                        {value:'AT_VEHICLE', key:'AT_VEHICLE', label: t('value.at_vehicle')}]}
                                optionType="button"
                                buttonStyle="solid"
                                onChange={(e) => {
                                    handleLocationChange(e.target.value);
                                }}
                            >
                            </Radio.Group>   
                            </Form.Item>
                            {
                                !addressRequired && (tyreWorkLocation && tyreWorkLocation !== undefined && tyreWorkLocation === 'AT_SUPPLIER')
                                ?
                                <Form.Item name="collectionDeliveryRequired" 
                                        label={t('field.collection_delivery_required')}
                                        layout='horizontal'
                                >
                                    <Switch checkedChildren={<CheckOutlined />}
                                            unCheckedChildren={<CloseOutlined />}
                                    />
                                </Form.Item>                    
                                :
                                <></>
                            }

                            {/* display address lookup if enabled or allow driver to input the address */}
                            {showAddressFinder 
                            ? 
                            <>
                            {!warningMessage ? <></> : <Alert showIcon message={warningMessage} type="warning" />}                   
                            <Form.Item
                                name="postcode"
                                label={t('field.postcode')}
                                rules={[{ 
                                    required: true, message: t('error.no_value_named', {0: t('field.postcode')})},
                                ]}
                            >
                                <Input                                
                                    label={t('field.postcode')}
                                    placeholder={t('action.enter_value')}
                                    type={'text'}    
                                />
                            </Form.Item>
                            <Form.Item
                                name="line1"
                                label={t('field.address_line_1')}
                                rules={[
                                ]}
                            >
                                <Input                                
                                    placeholder={t('action.enter_value')}
                                    type={'text'}    
                                />
                            </Form.Item>
                            {addressOptions && addressOptions.length > 0 ? 
                                <Form.Item
                                    name="searchResult"
                                    label={t('field.address_options')}
                                    rules={[{
                                        required: true, message: t('error.no_value_named', {0: t('field.address_options')})},
                                    ]}
                                >
                                    <Select loading={false} style={{ width: 300 }} onChange={(val) => selectAddress(val)}>                     
                                        {createSelectOptions(addressOptions)}
                                    </Select>  
                                </Form.Item>
                                :
                                <></>
                            }
                            </>                
                            :
                            <></> 
                            }
                            {
                                showAddress
                                ?
                                <>
                                <Form.Item
                                    name="line1"
                                    initialValue={selectedAddress?.line1}
                                    label={t('field.address_line_1')}
                                    rules={[{
                                        required: true, message: t('error.no_value_named', {0: t('field.address_line_1')})},
                                    ]}
                                >
                                    <Input                                
                                        placeholder={t('action.enter_value')}
                                        type={'text'}    
                                    />
                                </Form.Item>
                                    <Form.Item
                                        name="line2"
                                        label={t('field.address_line_2')}
                                        initialValue={selectedAddress?.line2}
                                        rules={[
                                        ]}
                                    >
                                        <Input                                
                                            placeholder={t('action.enter_value')}
                                            type={'text'}    
                                        />
                                    </Form.Item>
                                    <Form.Item
                                        name="line3"
                                        label={t('field.address_line_3')}
                                        initialValue={selectedAddress?.line3}
                                        rules={[
                                        ]}
                                    >
                                        <Input                                
                                            placeholder={t('action.enter_value')}
                                            type={'text'}    
                                        />
                                    </Form.Item>
                                    <Form.Item
                                        name="townOrCity"
                                        label={t('field.town_or_city')}
                                        initialValue={selectedAddress?.townOrCity}
                                        rules={[{
                                            required: true, message: t('error.no_value_named', {0: t('field.town_or_city')})},
                                        ]}
                                    >
                                        <Input                                
                                            placeholder={t('action.enter_value')}
                                            type={'text'}    
                                        />
                                    </Form.Item>
                                    <Form.Item
                                        name="region"
                                        label={t('field.region')}
                                        initialValue={selectedAddress?.region}
                                        rules={[
                                        ]}
                                    >
                                        <Input                                
                                            placeholder={t('action.enter_value')}
                                            type={'text'}    
                                        />
                                    </Form.Item>
                                    <Form.Item
                                        name="postcode"
                                        initialValue={selectedAddress?.postcode}
                                        label={t('field.postcode')}
                                        rules={[
                                        ]}
                                    >
                                        <Input                                
                                            placeholder={t('action.enter_value')}
                                            type={'text'}    
                                        />  
                                    </Form.Item>
                                    <Form.Item
                                        name="countryCode"
                                        initialValue={selectedAddress?.countryCode || countryInfo?.defaultCountryCode}
                                        label={t('field.country')}
                                        rules={[
                                        ]}
                                    >
                                        <Select placeholder={t('action.select_value')} loading={false}>
                                            {createSelectOptions(countryInfo?.countries)}
                                        </Select>
                                    </Form.Item>
                                </>                    
                                :
                                <></>
                            }
                            <div className="popup-buttons">
                                {
                                    showAddressFinder
                                    ?
                                    <>
                                    <Button type="link" onClick={() => {setSelectedAddress({...tyreRequestDetailsForm.getFieldsValue()})}} icon={<FormOutlined />}>
                                    {t('action.enter_manually')}
                                    </Button>
                                    <Button type="primary" onClick={() => {lookupAddress()}} loading={resultsLoading < 100} icon={<SearchOutlined />}>
                                    {t('action.search')}
                                    </Button>       
                                    </>
                                    :
                                    <ClearAddressLink showLink={showClearAddress} clearAddressFields={ClearAddress}>  </ClearAddressLink>    
                                }
                            </div>                 
                        </Form>
                    </div>
                </div>
        )
    }    


    const tyreRepairRequestsTableColumns = [
        {
            title: t('field.tyre'),
            dataIndex: 'axlePositionDescription',
            key: 'axlePositionDescription',
            render: (text) => <Text>{text}</Text>
        },
        {
            title: t('field.work'),
            dataIndex: 'tyreReviewOutcome',
            key: 'tyreReviewOutcome',
            align:'center',
            render: (tyreReviewOutcome) => {
                let color = tyreReviewOutcome === 'REPLACE' ? 'volcano' : 'blue';
                let translatableValue = tyreReviewOutcome === 'REPLACE' ? t('action.replace') : t('action.repair');
                
                return (
                    <Tag color={color} key={tyreReviewOutcome}>
                      {translatableValue}
                    </Tag>
                  )
            }
        },
        {
            title: t('field.description'),
            dataIndex: 'replacementTyreBrand',
            key: 'replacementTyreBrand',
            render: (text, record) =>  {
                let tyreDescription = record.tyreReviewOutcome === 'REPLACE' ? record.replacementTyreBrand + ' , ' +record.replacementTyreSize : null;
                
                return (
                    <Text className="expandable-col-content" >
                        {tyreDescription}
                    </Text>
                )
            }
        },
    ];
    
    const TyreRepairRequestsTable = () => {

        return (
            <Row>
                <Col flex="100%">
                        <Table className="dp-simple-border" 
                               style={{ marginBottom: '20px'}} 
                               columns={tyreRepairRequestsTableColumns} 
                               dataSource={tyresBeingRepaired} 
                               pagination={false}  
                        />
                </Col>
            </Row>
        );
    };

    return (
        <Modal className="std-portal-popup"
               open={tyreRequestDetailsModalVisible}
               title={t('title.tyre_request')}
               closable={true}
               maskClosable={false}
               onCancel={() => {
                  setTyreRequestDetailsModalVisible(false); 
                  notification.destroy();
                  setCurrentTyreRequest({});
                  tyreRequestDetailsForm.resetFields();
                  setAddressRequired(false);
                  setTyreWorkLocation(null);
                }}
               afterClose={ () => {
               }}
               keyboard={false}
               footer={
                [
                    <Button key="back" 
                            onClick={(e) => {
                                setTyreRequestDetailsModalVisible(false); 
                                setIsModalVisible(true); 
                                notification.destroy();
                            }}
                    >
                     {t('action.back')}
                    </Button>,
                    <Button type="primary" 
                            form="tyreRequestDetails" 
                            key="submit" 
                            htmlType="submit" 
                            loading={submitProgress < 100}
                    >
                     {t('action.submit')}
                    </Button>,
                  ]
                      }
        >
            <TyreRepairRequestsTable></TyreRepairRequestsTable>
            <TyreRequestForm onFinish={onFinish}
                             driver={driver}
            />          

        </Modal>          

    );
}

export default TyreRequestDetails;